import { axiosInstance } from '@/utils/libs/axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { reportSchema } from './report';
import { ITestRunSchema } from './testRun';
import { IUserSchema } from './user';
import { fileSchemaOptional } from '@/utils/libs/schemaUtil';

export const releaseSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().max(255),
    start_date: z.string(),
    end_date: z.string(),
    medias: fileSchemaOptional.optional(),
    project_id: z.string().optional(),
    report: reportSchema.optional(),
    created_at: z.string().optional(),
  })
  .refine(data => new Date(data.start_date) <= new Date(data.end_date), {
    message: 'Start date cannot be greater than end date',
    path: ['start_date'], // This points to the field causing the issue
  });

export type IReleaseSchema = z.infer<typeof releaseSchema> & {
  test_run?: ITestRunSchema;
  created_by?: IUserSchema;
  updated_by?: IUserSchema;
};

export type IReportSchema = z.infer<typeof reportSchema>;

export const ReleaseService = {
  useGetList: (projectId?: string) =>
    useQuery<{ id: string; name: string }[]>({
      queryKey: ['release-list', projectId],
      queryFn: async () => {
        if (!projectId) return [];

        const response = await axiosInstance.get('/api/crud/release', {
          params: { filter: { project_id: projectId } },
        });

        return response.data?.data?.content ?? [];
      },
      enabled: !!projectId,
    }),

  useGet: (id?: string) =>
    useQuery<IReleaseSchema>({
      queryKey: ['release', id],
      queryFn: async () => {
        if (!id) return {};

        const response = await axiosInstance.get(`/api/crud/release/${id}`);

        return response.data?.data;
      },
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: IReleaseSchema) => {
        const res = await axiosInstance.post('/api/crud/release', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const res = await axiosInstance.delete(`/api/crud/release/${id}`);
        return res.data;
      },
    }),

  useArchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/release/${id}/archive`,
        );

        return response.data;
      },
    }),

  useUnarchive: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const response = await axiosInstance.post(
          `/api/crud/release/${id}/unarchive`,
        );
        return response.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: IReleaseSchema) => {
        const res = await axiosInstance.post(
          `/api/crud/release/${data.id}`,
          data,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return res.data;
      },
    }),
};
